import React, { useCallback, useEffect, useRef, useState } from 'react';
import { finishPasswordStepUp } from '../auth/passwordStepUp';

export default function PasswordStepUpDialog({ accountUrl, callbackOrigin }) {
  const [visible, setVisible] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [statusError, setStatusError] = useState('');
  const popup = useRef(null);
  const expectedOrigin = useRef('');
  const statusTimer = useRef(null);
  const statusRequest = useRef(null);
  const requestTimeout = useRef(null);
  const attempt = useRef(null);
  const stopPolling = useCallback(() => {
    attempt.current = null;
    clearTimeout(statusTimer.current);
    statusTimer.current = null;
    clearTimeout(requestTimeout.current);
    requestTimeout.current = null;
    statusRequest.current?.abort();
    statusRequest.current = null;
  }, []);
  const complete = useCallback(() => {
    stopPolling();
    finishPasswordStepUp(true);
    setVisible(false);
  }, [stopPolling]);
  useEffect(() => {
    const required = () => { stopPolling(); setVisible(true); setBlocked(false); setStatusError(''); };
    const verified = (event) => {
      if (event.data?.type !== 'ail-password-verified' || event.source !== popup.current
          || !popup.current || event.origin !== expectedOrigin.current
          || event.data.challenge !== attempt.current?.challenge) return;
      complete();
    };
    window.addEventListener('ail-password-required', required);
    window.addEventListener('message', verified);
    return () => {
      window.removeEventListener('ail-password-required', required);
      window.removeEventListener('message', verified);
      stopPolling();
      finishPasswordStepUp(false);
    };
  }, [complete, stopPolling]);
  if (!visible) return null;
  const verify = () => {
    const base = process.env.REACT_APP_API_BASE_URL || '/api';
    const returnTo = window.location.pathname + window.location.search + window.location.hash;
    stopPolling();
    setStatusError('');
    // This is only a random notification correlation ID. No account/session
    // identity or credential is placed in a URL or browser storage.
    const challenge = Array.from(window.crypto.getRandomValues(new Uint8Array(16)),
      value => value.toString(16).padStart(2, '0')).join('');
    const url = base + '/auth/sso/step-up/?' + new URLSearchParams({ returnTo, popup: '1', challenge });
    // The OIDC callback can use a different public origin from the API proxy.
    // Accept only the server-configured origin and the exact popup window.
    expectedOrigin.current = new URL(callbackOrigin || url, window.location.href).origin;
    popup.current = window.open(url, 'ail-password-verification', 'popup,width=560,height=760');
    setBlocked(!popup.current);
    if (!popup.current) return;
    const current = { challenge, expiresAt: Date.now() + 120000 };
    attempt.current = current;
    const poll = async () => {
      if (attempt.current !== current) return;
      if (Date.now() >= current.expiresAt) {
        stopPolling();
        setStatusError('自动检查已结束。验证完成后可点击“已完成，重新检查”。');
        return;
      }
      const controller = new AbortController();
      const requestTimer = setTimeout(() => controller.abort(), Math.min(3000, current.expiresAt - Date.now()));
      requestTimeout.current = requestTimer;
      statusRequest.current = controller;
      try {
        // Raw fetch avoids recursing through the protected API interceptor.
        const response = await fetch(base + '/auth/sso/step-up/status/?' + new URLSearchParams({ challenge }), {
          credentials: 'include', cache: 'no-store', signal: controller.signal,
        });
        if (response.ok && (await response.json()).verified === true && attempt.current === current) {
          complete();
          return;
        }
      } catch {
        if (attempt.current !== current) return;
      } finally {
        clearTimeout(requestTimer);
        if (requestTimeout.current === requestTimer) requestTimeout.current = null;
        if (statusRequest.current === controller) statusRequest.current = null;
      }
      if (attempt.current === current) {
        if (Date.now() >= current.expiresAt) {
          stopPolling();
          setStatusError('自动检查已结束。验证完成后可点击“已完成，重新检查”。');
        } else statusTimer.current = setTimeout(poll, 1000);
      }
    };
    poll();
  };
  return <div role="dialog" aria-modal="true" aria-labelledby="password-step-up-title"
    style={{ position: 'fixed', inset: 0, zIndex: 10000, display: 'grid', placeItems: 'center', background: 'rgba(0,0,0,.4)' }}>
    <div style={{ background: 'white', padding: 24, borderRadius: 8, maxWidth: 460, margin: 16 }}>
      <h2 id="password-step-up-title">请重新验证密码</h2>
      <p>当前页面和未保存内容会保留。验证完成后，读取会继续；保存或删除请再次点击确认。</p>
      <p>手机号注册后尚未设置密码？请先到账号中心设置密码，再重新登录。</p>
      {accountUrl && <p><a href={accountUrl} target="_blank" rel="noopener noreferrer">前往账号中心设置密码</a></p>}
      {blocked && <p role="alert">验证窗口被浏览器拦截，请允许弹出窗口后再点击验证。</p>}
      {statusError && <p role="alert">{statusError}</p>}
      <button type="button" onClick={verify}>验证密码</button>{' '}
      <button type="button" onClick={complete}>已完成，重新检查</button>{' '}
      <button type="button" onClick={() => { stopPolling(); finishPasswordStepUp(false); setVisible(false); }}>稍后验证</button>
    </div>
  </div>;
}
